import { z } from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { clientIdSchema, paymentIdSchema, refundIdSchema, sessionChargeIdSchema } from './ids';

export const PAYMENT_RECORD_TYPE = 'payment/record';
export const PAYMENT_REFUND_TYPE = 'payment/refund';

export const PAYMENT_METHODS = ['CASH', 'CHECK', 'EXTERNAL_CARD', 'CARD_ON_FILE'] as const;

export const paymentAllocationInputSchema = z.object({
    sessionChargeId: sessionChargeIdSchema,
    amountCents: z.number().int().min(1),
});

export const paymentRecordObjectSchema = z.object({
    id: paymentIdSchema.meta(metadata.nonSensitive),
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    clientId: clientIdSchema.meta(metadata.nonSensitive),
    method: z.enum(PAYMENT_METHODS).exclude(['CARD_ON_FILE']).meta(metadata.nonSensitive),
    checkNumber: z.string().trim().min(1).max(30).optional(),
    paidOn: z.iso.date().optional(),
    allocations: z.array(paymentAllocationInputSchema).min(1),
});

export const refinePaymentRecord = (
    value: Pick<
        z.input<typeof paymentRecordObjectSchema>,
        'method' | 'checkNumber' | 'allocations'
    >,
    ctx: z.core.$RefinementCtx,
) => {
    if (value.checkNumber !== undefined && value.method !== 'CHECK') {
        ctx.addIssue({
            code: 'custom',
            message: 'A check number is only allowed for a check payment',
            path: ['checkNumber'],
        });
    }

    const seen = new Set<string>();
    value.allocations.forEach((allocation, index) => {
        if (seen.has(allocation.sessionChargeId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'A session can be paid only once per payment',
                path: ['allocations', index, 'sessionChargeId'],
            });
        }
        seen.add(allocation.sessionChargeId);
    });
};

export const paymentRecord = defineCommand(
    PAYMENT_RECORD_TYPE,
    paymentRecordObjectSchema.superRefine(refinePaymentRecord),
    z.object({ id: paymentIdSchema.meta(metadata.nonSensitive) }),
);

export const paymentRefundObjectSchema = z.object({
    id: refundIdSchema.meta(metadata.nonSensitive),
    paymentId: paymentIdSchema.meta(metadata.nonSensitive),
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    clientId: clientIdSchema.meta(metadata.nonSensitive),
    method: z.enum(PAYMENT_METHODS).exclude(['CARD_ON_FILE']).meta(metadata.nonSensitive),
    checkNumber: z.string().trim().min(1).max(30).optional(),
    refundedOn: z.iso.date().optional(),
});

export const refinePaymentRefund = (
    value: Pick<z.input<typeof paymentRefundObjectSchema>, 'method' | 'checkNumber'>,
    ctx: z.core.$RefinementCtx,
) => {
    if (value.checkNumber !== undefined && value.method !== 'CHECK') {
        ctx.addIssue({
            code: 'custom',
            message: 'A check number is only allowed for a check refund',
            path: ['checkNumber'],
        });
    }
};

export const paymentRefund = defineCommand(
    PAYMENT_REFUND_TYPE,
    paymentRefundObjectSchema.superRefine(refinePaymentRefund),
    z.object({ id: refundIdSchema.meta(metadata.nonSensitive) }),
);
